import React from 'react';
import { SetupHelpPanel } from './SetupHelpPanel';

interface WarehouseSetupHelpProps {
  /**
   * Warehouse name entered by user (for dynamic SQL)
   */
  warehouseName: string;
  /**
   * Role name entered by user (for dynamic SQL in GRANT statement)
   */
  roleName: string;
}

/**
 * WarehouseSetupHelp Component
 *
 * Displays setup instructions for creating a Snowflake warehouse with
 * dynamic SQL that updates based on user input.
 *
 * @component
 * @example
 * ```tsx
 * <WarehouseSetupHelp
 *   warehouseName="DATAOPS_ADMIN_WAREHOUSE"
 *   roleName="DATAOPS_ADMIN_ROLE"
 * />
 * ```
 */
export const WarehouseSetupHelp: React.FC<WarehouseSetupHelpProps> = ({ warehouseName, roleName }) => {
  // Use defaults if values are empty
  const displayWarehouseName = warehouseName.trim() !== '' ? warehouseName.trim() : 'DATAOPS_ADMIN_WAREHOUSE';
  const displayRoleName = roleName.trim() !== '' ? roleName.trim() : 'DATAOPS_ADMIN_ROLE';

  const warehouseSQL = `-- Create a small, resumable warehouse for DataOps admin tasks
CREATE WAREHOUSE IF NOT EXISTS ${displayWarehouseName}
    WITH WAREHOUSE_SIZE = 'X-SMALL'
         AUTO_SUSPEND = 60
         AUTO_RESUME = TRUE
         INITIALLY_SUSPENDED = TRUE
         COMMENT = 'DataOps admin warehouse';

-- Grant minimal access to the DataOps admin role
GRANT USAGE, OPERATE, MONITOR
    ON WAREHOUSE ${displayWarehouseName}
    TO ROLE ${displayRoleName};`;

  return (
    <SetupHelpPanel
      id="warehouse-setup-help"
      title="Setup help"
      purpose="Create a dedicated, cost-controlled warehouse DataOps can use."
      sqlBlocks={[
        {
          code: warehouseSQL,
          runAsRole: 'ACCOUNTADMIN or equivalent',
        },
      ]}
      nextSteps={[
        'Paste and run in a worksheet using ACCOUNTADMIN (or equivalent).',
        `Enter "${displayWarehouseName}" in the Warehouse field here.`,
      ]}
      notes={[
        'You can change size or suspend policy to fit your budget.',
        `If your role name differs, update ${displayRoleName} in the snippet.`,
      ]}
      storageKey="warehouse-help-expanded"
    />
  );
};
